import cx from 'clsx';
import React from 'react';
import { type Control, useWatch } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import { toCurrency } from '@helpers/helper';

import useTranslation from '@hooks/useTranslation';

import type { IAiCostSavingSettings } from '@model/aiCostSavingSettings';
import type { ICauseCategorySeverity, IReplaceParts } from '@model/aiReport';
import type { ICurrency } from '@model/currency';

import { useActionItemMetaInfoQuery } from '@services/useActionItemMetaInfoQuery';
import { useCustomerInfoQuery } from '@services/useCustomerInfoQuery';

import { selectAppLocationSelectValue } from '@store/useAppStore';

import { COMPLETION_REPORT_FIELDS } from '../constants/schemaCompletionReport';
import VerifyValueItem from './VerifyValueItem/VerifyValueItem';
import style from './style.module.scss';

interface IProps {
  control: Control;
  costAvoided: number;
  noActionsSwitcher: number;
  isWwAi?: boolean;
  costSavingSettings: IAiCostSavingSettings;
  currency: ICurrency;
}

const StepVerifyReport = ({
  control,
  costAvoided,
  noActionsSwitcher,
  isWwAi,
  costSavingSettings,
  currency,
}: IProps) => {
  const { t } = useTranslation();
  const { customerId } = useParams();
  const locationSelectValue = selectAppLocationSelectValue();

  const { data: customerInfoData } = useCustomerInfoQuery({
    customerId,
    locationIds:
      locationSelectValue.level_name === 'allFacilities' ? undefined : locationSelectValue?.filterIds,
  });
  const { data: aiMetaInfoData } = useActionItemMetaInfoQuery();

  const [
    text,
    cause,
    causeCategorySeverities,
    downtimeAvoided,
    downtimeType,
    lph_avoided,
    equipmentLaborSaving,
    replacedParts,
    anyRepairsOrMaintenance,
    noActionsGroupId,
    noActionsGroupOtherText,
    fileName,
  ] = useWatch({
    control,
    name: [
      COMPLETION_REPORT_FIELDS.TEXT,
      COMPLETION_REPORT_FIELDS.CAUSE,
      COMPLETION_REPORT_FIELDS.CAUSE_CATEGORY_SEVERITIES,
      COMPLETION_REPORT_FIELDS.DOWNTIME_AVOIDED,
      COMPLETION_REPORT_FIELDS.DOWNTIME_TYPE,
      COMPLETION_REPORT_FIELDS.LPH_AVOIDED,
      COMPLETION_REPORT_FIELDS.EQUIPMENT_LABOR_SAVINGS,
      COMPLETION_REPORT_FIELDS.REPLACED_PARTS,
      COMPLETION_REPORT_FIELDS.ANY_REPAIRS_OR_MAINTENANCE,
      COMPLETION_REPORT_FIELDS.NO_ACTIONS_GROUP_ID,
      COMPLETION_REPORT_FIELDS.NO_ACTIONS_GROUP_OTHER_TEXT,
      COMPLETION_REPORT_FIELDS.FILE_NAME,
    ],
  });

  const hasLphAvoided = !!costSavingSettings?.use_lph_avoided;
  const multiplicationFactor =
    costSavingSettings.multiplication_factor_in_currency ?? costSavingSettings.multiplication_factor;

  const hasReplacedParts = replacedParts.find(
    (part: IReplaceParts) => part.nameOfOem || part.amazonPart || part.amazonEamPart,
  );

  return (
    <div className={cx(style.stepVerify, style.scrollingBlock)}>
      <VerifyValueItem label={'Description of maintenance/actions taken'} valueHtml={text} dataQa={'text'} />
      {isWwAi && anyRepairsOrMaintenance === 'maintenance' && (
        <VerifyValueItem
          label={'Attachments'}
          value={`${(fileName || []).length} ${t((fileName || []).length === 1 ? 'file' : 'files')}`}
          dataQa={'file_name'}
        />
      )}
      {!isWwAi && anyRepairsOrMaintenance === 'maintenance' && (
        <>
          <VerifyValueItem
            label={'Reason'}
            valueHtml={aiMetaInfoData?.actionListNoActionsGroups.byKey[noActionsGroupId] || ''}
            dataQa={'reason'}
          />
          {+noActionsGroupId === 6 && (
            <VerifyValueItem
              label={'Other description'}
              valueHtml={noActionsGroupOtherText}
              dataQa={'no_actions_text'}
            />
          )}
        </>
      )}
      {noActionsSwitcher === 0 && (
        <>
          <VerifyValueItem label={'Cause description'} valueHtml={cause} dataQa={'cause'} />
          <div>
            <div className={style.twoParts}>
              <div className={style.fieldLabel}>{t('Cause Category')}</div>
              <div className={style.fieldLabel}>{t('Severity level')}</div>
            </div>
            {causeCategorySeverities.map((category: ICauseCategorySeverity, i: number) => (
              <div key={uuidv4()} className={cx(style.twoParts, style.valuesItems)}>
                <VerifyValueItem value={category.cause_category.label} dataQa={'cause_category'} />
                <VerifyValueItem value={category.severity.name} dataQa={'severity'} />
              </div>
            ))}
          </div>
          <div className={style.twoParts}>
            <div className={style.twoParts}>
              <VerifyValueItem
                label={'Unplanned downtime avoided'}
                value={`${downtimeAvoided} ${t(downtimeType === 0 ? 'hours' : 'minutes')}`}
                dataQa={'downtime_avoided'}
              />
              <VerifyValueItem
                label={'Equipment/Labor savings'}
                value={`${currency.symbol} ${toCurrency(equipmentLaborSaving)}`}
                dataQa={'equipment_labor_saving'}
              />
              {hasLphAvoided && (
                <VerifyValueItem
                  label={'Lph Avoided'}
                  value={`${lph_avoided} ${t(downtimeType === 0 ? 'hours' : 'minutes')}`}
                  dataQa={'lph_avoided'}
                />
              )}
              {customerInfoData?.showTotalCostSavings && (
                <>
                  <VerifyValueItem
                    label={'Multiplication factor'}
                    value={`${currency.symbol} ${toCurrency(multiplicationFactor)}`}
                    dataQa={'multiplication_factor'}
                  />
                  <VerifyValueItem
                    label={'Cost avoided'}
                    value={`${currency.symbol} ${toCurrency(costAvoided)}`}
                    dataQa={'cost_avoided'}
                  />
                </>
              )}
            </div>
            <div className={style.attachFieldWrapper}>
              <VerifyValueItem
                label={'Attachments'}
                value={`${(fileName || []).length} ${t((fileName || []).length === 1 ? 'file' : 'files')}`}
                variant="full-height"
                dataQa={'file_name'}
              />
            </div>
          </div>
          {!!hasReplacedParts && (
            <div>
              <label className={style.fieldLabel}>{t('Replacement parts')}</label>
              {replacedParts.map((part: IReplaceParts, i: number) => (
                <div key={uuidv4()} className={cx(style.threeParts, style.replacedParts)}>
                  <VerifyValueItem value={part.nameOfOem} dataQa={'name_of_oem'} />
                  <VerifyValueItem value={part.amazonPart} dataQa={'amazon_part'} />
                  <VerifyValueItem value={part.amazonEamPart} dataQa={'amazon_eam_part'} />
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default StepVerifyReport;
